import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../lib/api';
import { 
  Ticket, ShoppingCart, BarChart3, LogOut, Plus, 
  Check, X, MessageCircle, Trash2, Edit, Eye, EyeOff, Download 
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

// Get PDF URL helper
const getPdfUrl = (purchaseId) => `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${purchaseId}/pdf`;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('purchases');
  const [stats, setStats] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRaffleModal, setShowRaffleModal] = useState(false);
  const [editingRaffle, setEditingRaffle] = useState(null);
  const [raffleForm, setRaffleForm] = useState({
    title: '',
    description: '',
    prize_description: '',
    image_url: 'https://images.pexels.com/photos/17377918/pexels-photo-17377918.jpeg',
    ticket_price: 100,
    total_tickets: 100,
    start_ticket: 1,
    end_date: '',
    bank_details: '',
    whatsapp_number: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, rafflesRes, purchasesRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getRaffles(),
        adminApi.getPurchases()
      ]);
      setStats(statsRes.data);
      setRaffles(rafflesRes.data);
      setPurchases(purchasesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleCreateRaffle = async (e) => {
    e.preventDefault();
    try {
      if (editingRaffle) {
        await adminApi.updateRaffle(editingRaffle.id, raffleForm);
        toast.success('Sorteo actualizado');
      } else {
        await adminApi.createRaffle(raffleForm);
        toast.success('Sorteo creado');
      }
      setShowRaffleModal(false);
      setEditingRaffle(null);
      setRaffleForm({
        title: '',
        description: '',
        prize_description: '',
        image_url: 'https://images.pexels.com/photos/17377918/pexels-photo-17377918.jpeg',
        ticket_price: 100,
        total_tickets: 100,
        start_ticket: 1,
        end_date: '',
        bank_details: '',
        whatsapp_number: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el sorteo');
    }
  };

  const handleEditRaffle = (raffle) => {
    setEditingRaffle(raffle);
    setRaffleForm({
      title: raffle.title,
      description: raffle.description,
      prize_description: raffle.prize_description,
      image_url: raffle.image_url,
      ticket_price: raffle.ticket_price,
      total_tickets: raffle.total_tickets,
      start_ticket: raffle.start_ticket || 1,
      end_date: raffle.end_date,
      bank_details: raffle.bank_details || '',
      whatsapp_number: raffle.whatsapp_number || ''
    });
    setShowRaffleModal(true);
  };

  const handleDeleteRaffle = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este sorteo?')) return;
    try {
      await adminApi.deleteRaffle(id);
      toast.success('Sorteo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el sorteo');
    }
  };

  const handleToggleRaffleStatus = async (raffle) => {
    try {
      await adminApi.updateRaffle(raffle.id, { is_active: !raffle.is_active });
      toast.success(raffle.is_active ? 'Sorteo desactivado' : 'Sorteo activado');
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleConfirmPurchase = async (id) => {
    try {
      await adminApi.confirmPurchase(id);
      toast.success('Compra confirmada');
      fetchData();
    } catch (error) {
      toast.error('Error al confirmar');
    }
  };

  const handleCancelPurchase = async (id) => {
    if (!window.confirm('¿Cancelar esta compra? Los boletos quedarán disponibles.')) return;
    try {
      await adminApi.cancelPurchase(id);
      toast.success('Compra cancelada');
      fetchData();
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const generateWhatsAppLink = (purchase) => {
    const message = `Hola ${purchase.buyer_name}! Tu compra de boletos ${purchase.tickets.join(', ')} del sorteo "${purchase.raffle_title}" ha sido confirmada. ¡Mucha suerte!`;
    return `https://wa.me/${purchase.buyer_phone}?text=${encodeURIComponent(message)}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-black border-t-transparent"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 w-64 h-screen bg-white border-r-2 border-black z-40" data-testid="admin-sidebar">
        <div className="p-6 border-b-2 border-black">
          <Link to="/" className="font-black text-xl tracking-tighter">
            ZEIT<span className="text-[#FF3333]">EX</span>
          </Link>
          <p className="text-xs text-[#4B5563] mt-1">Panel Admin</p>
        </div>

        <nav className="p-4 space-y-2">
          <button
            onClick={() => setActiveTab('purchases')}
            className={`w-full flex items-center gap-3 px-4 py-3 font-bold text-sm transition-all ${
              activeTab === 'purchases' 
                ? 'bg-[#0A0A0A] text-white' 
                : 'hover:bg-[#F3F4F6]'
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            Compras
          </button>
          <button
            onClick={() => setActiveTab('raffles')}
            className={`w-full flex items-center gap-3 px-4 py-3 font-bold text-sm transition-all ${
              activeTab === 'raffles' 
                ? 'bg-[#0A0A0A] text-white' 
                : 'hover:bg-[#F3F4F6]'
            }`}
          >
            <Ticket className="w-5 h-5" />
            Sorteos
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`w-full flex items-center gap-3 px-4 py-3 font-bold text-sm transition-all ${
              activeTab === 'stats' 
                ? 'bg-[#0A0A0A] text-white' 
                : 'hover:bg-[#F3F4F6]'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            Estadísticas
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t-2 border-black">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-sm">{user.name}</p>
              <p className="text-xs text-[#4B5563]">{user.email}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="p-2 hover:bg-[#F3F4F6] transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0_0_#000000]">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">Sorteos Activos</p>
              <p className="text-3xl font-black mt-2">{stats.active_raffles}</p>
            </div>
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0_0_#000000]">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">Compras Pendientes</p>
              <p className="text-3xl font-black mt-2 text-[#F59E0B]">{stats.pending_purchases}</p>
            </div>
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0_0_#000000]">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">Confirmadas</p>
              <p className="text-3xl font-black mt-2 text-[#10B981]">{stats.confirmed_purchases}</p>
            </div>
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0_0_#FF3333]">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">Ingresos</p>
              <p className="text-3xl font-black mt-2 text-[#FF3333]">${stats.total_revenue.toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="bg-white border-2 border-black shadow-[8px_8px_0_0_#000000]">
            <div className="p-6 border-b-2 border-black flex items-center justify-between">
              <h2 className="text-2xl font-bold">Compras</h2>
              <Tabs defaultValue="all" className="w-auto">
                <TabsList className="border-2 border-black rounded-none">
                  <TabsTrigger value="all" className="rounded-none">Todas</TabsTrigger>
                  <TabsTrigger value="pending" className="rounded-none">Pendientes</TabsTrigger>
                  <TabsTrigger value="confirmed" className="rounded-none">Confirmadas</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="overflow-x-auto" data-testid="purchases-table">
              <table className="w-full">
                <thead className="bg-[#F3F4F6] border-b-2 border-black">
                  <tr>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Cliente</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Sorteo</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Boletos</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Total</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Estado</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((purchase) => (
                    <tr key={purchase.id} className="border-b border-gray-200 hover:bg-[#F3F4F6]">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold">{purchase.buyer_name}</p>
                          <p className="text-xs text-[#4B5563]">{purchase.buyer_phone}</p>
                          <p className="text-xs text-[#4B5563]">{purchase.buyer_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">{purchase.raffle_title}</td>
                      <td className="px-6 py-4 text-sm font-mono">{purchase.tickets.join(', ')}</td>
                      <td className="px-6 py-4 font-bold">${purchase.total_amount}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-bold uppercase ${
                          purchase.status === 'confirmed' 
                            ? 'bg-[#D1FAE5] text-[#065F46]' 
                            : 'bg-[#FEF3C7] text-[#92400E]'
                        }`}>
                          {purchase.status === 'confirmed' ? 'Confirmada' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {purchase.status === 'pending' && (
                            <button
                              onClick={() => handleConfirmPurchase(purchase.id)}
                              className="p-2 bg-[#10B981] text-white hover:bg-[#059669] transition-colors"
                              title="Confirmar pago"
                              data-testid="confirm-payment-btn"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <a
                            href={getPdfUrl(purchase.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-[#0022FF] text-white hover:bg-[#001ACC] transition-colors"
                            title="Descargar PDF"
                            data-testid="download-pdf-btn"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <a
                            href={generateWhatsAppLink(purchase)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-[#25D366] text-white hover:bg-[#1da851] transition-colors"
                            title="Enviar WhatsApp"
                            data-testid="send-whatsapp-btn"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleCancelPurchase(purchase.id)}
                            className="p-2 bg-[#EF4444] text-white hover:bg-[#DC2626] transition-colors"
                            title="Cancelar compra"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {purchases.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-[#4B5563]">
                        No hay compras registradas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Raffles Tab */}
        {activeTab === 'raffles' && (
          <div className="bg-white border-2 border-black shadow-[8px_8px_0_0_#000000]">
            <div className="p-6 border-b-2 border-black flex items-center justify-between">
              <h2 className="text-2xl font-bold">Sorteos</h2>
              <button
                onClick={() => {
                  setEditingRaffle(null);
                  setRaffleForm({
                    title: '',
                    description: '',
                    prize_description: '',
                    image_url: 'https://images.pexels.com/photos/17377918/pexels-photo-17377918.jpeg',
                    ticket_price: 100,
                    total_tickets: 100,
                    start_ticket: 1,
                    end_date: '',
                    bank_details: '',
                    whatsapp_number: ''
                  });
                  setShowRaffleModal(true);
                }}
                className="flex items-center gap-2 bg-[#FF3333] text-white font-bold uppercase tracking-wider border-2 border-black px-4 py-2 shadow-[4px_4px_0_0_#000000] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_0_#000000] transition-all"
              >
                <Plus className="w-4 h-4" /> Nuevo Sorteo
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F3F4F6] border-b-2 border-black">
                  <tr>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Sorteo</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Precio</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Boletos</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Ingresos</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Estado</th>
                    <th className="text-left px-6 py-4 font-bold text-xs uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {raffles.map((raffle) => (
                    <tr key={raffle.id} className="border-b border-gray-200 hover:bg-[#F3F4F6]">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={raffle.image_url} 
                            alt={raffle.title}
                            className="w-12 h-12 object-cover border-2 border-black"
                          />
                          <div>
                            <p className="font-bold">{raffle.title}</p>
                            <p className="text-xs text-[#4B5563]">Termina: {new Date(raffle.end_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold">${raffle.ticket_price}</td>
                      <td className="px-6 py-4">
                        <span className="font-bold">{raffle.sold_tickets}</span>
                        <span className="text-[#4B5563]">/{raffle.total_tickets}</span>
                      </td>
                      <td className="px-6 py-4 font-bold text-[#10B981]">${raffle.total_revenue?.toLocaleString() || 0}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-bold uppercase ${
                          raffle.is_active 
                            ? 'bg-[#D1FAE5] text-[#065F46]' 
                            : 'bg-[#E5E7EB] text-[#4B5563]'
                        }`}>
                          {raffle.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleRaffleStatus(raffle)}
                            className={`p-2 ${raffle.is_active ? 'bg-gray-500' : 'bg-[#10B981]'} text-white transition-colors`}
                            title={raffle.is_active ? 'Desactivar' : 'Activar'}
                          >
                            {raffle.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleEditRaffle(raffle)}
                            className="p-2 bg-[#0022FF] text-white hover:bg-[#001ACC] transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRaffle(raffle.id)}
                            className="p-2 bg-[#EF4444] text-white hover:bg-[#DC2626] transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {raffles.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-[#4B5563]">
                        No hay sorteos creados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-black p-6 shadow-[8px_8px_0_0_#000000]">
              <h3 className="text-xl font-bold mb-4">Resumen General</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-[#4B5563]">Total de Sorteos</span>
                  <span className="font-bold">{stats.total_raffles}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-[#4B5563]">Sorteos Activos</span>
                  <span className="font-bold">{stats.active_raffles}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-[#4B5563]">Total de Compras</span>
                  <span className="font-bold">{stats.total_purchases}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-[#4B5563]">Compras Pendientes</span>
                  <span className="font-bold text-[#F59E0B]">{stats.pending_purchases}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#4B5563]">Compras Confirmadas</span>
                  <span className="font-bold text-[#10B981]">{stats.confirmed_purchases}</span>
                </div>
              </div>
            </div>
            <div className="bg-[#0A0A0A] text-white border-2 border-black p-6 shadow-[8px_8px_0_0_#FF3333]">
              <h3 className="text-xl font-bold mb-4">Ingresos Totales</h3>
              <p className="text-5xl font-black text-[#FF3333]">${stats.total_revenue.toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-2">MXN de compras confirmadas</p>
            </div>
          </div>
        )}
      </main>

      {/* Raffle Modal */}
      <Dialog open={showRaffleModal} onOpenChange={setShowRaffleModal}>
        <DialogContent className="sm:max-w-2xl border-2 border-black rounded-none shadow-[8px_8px_0_0_#000000] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingRaffle ? 'Editar Sorteo' : 'Nuevo Sorteo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRaffle} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">Título</Label>
                <Input
                  value={raffleForm.title}
                  onChange={(e) => setRaffleForm({ ...raffleForm, title: e.target.value })}
                  required
                  className="border-2 border-black rounded-none"
                  placeholder="Ej: Auto deportivo 2024"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">Descripción</Label>
                <textarea
                  value={raffleForm.description}
                  onChange={(e) => setRaffleForm({ ...raffleForm, description: e.target.value })}
                  required
                  className="w-full border-2 border-black rounded-none px-4 py-3 min-h-[80px]"
                  placeholder="Descripción del sorteo..."
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">Descripción del Premio</Label>
                <textarea
                  value={raffleForm.prize_description}
                  onChange={(e) => setRaffleForm({ ...raffleForm, prize_description: e.target.value })}
                  required
                  className="w-full border-2 border-black rounded-none px-4 py-3 min-h-[60px]"
                  placeholder="Detalles del premio..."
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">URL de Imagen</Label>
                <Input
                  value={raffleForm.image_url}
                  onChange={(e) => setRaffleForm({ ...raffleForm, image_url: e.target.value })}
                  required
                  className="border-2 border-black rounded-none"
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">Precio por Boleto (MXN)</Label>
                <Input
                  type="number"
                  value={raffleForm.ticket_price}
                  onChange={(e) => setRaffleForm({ ...raffleForm, ticket_price: Number(e.target.value) })}
                  required
                  min="1"
                  className="border-2 border-black rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">Boleto Inicial</Label>
                <Input
                  type="number"
                  value={raffleForm.start_ticket}
                  onChange={(e) => setRaffleForm({ ...raffleForm, start_ticket: Number(e.target.value) })}
                  required
                  min="0"
                  className="border-2 border-black rounded-none"
                  placeholder="Ej: 1 o 0"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">Total de Boletos</Label>
                <Input
                  type="number"
                  value={raffleForm.total_tickets}
                  onChange={(e) => setRaffleForm({ ...raffleForm, total_tickets: Number(e.target.value) })}
                  required
                  min="1"
                  max="10000"
                  className="border-2 border-black rounded-none"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">Fecha de Cierre</Label>
                <Input
                  type="date"
                  value={raffleForm.end_date}
                  onChange={(e) => setRaffleForm({ ...raffleForm, end_date: e.target.value })}
                  required
                  className="border-2 border-black rounded-none"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">Datos Bancarios</Label>
                <textarea
                  value={raffleForm.bank_details}
                  onChange={(e) => setRaffleForm({ ...raffleForm, bank_details: e.target.value })}
                  required
                  className="w-full border-2 border-black rounded-none px-4 py-3 min-h-[80px]"
                  placeholder="Banco: BBVA&#10;Cuenta: 1234567890&#10;CLABE: 012345678901234567&#10;Titular: Juan Pérez"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="font-bold">Número de WhatsApp (con código de país)</Label>
                <Input
                  value={raffleForm.whatsapp_number}
                  onChange={(e) => setRaffleForm({ ...raffleForm, whatsapp_number: e.target.value })}
                  required
                  className="border-2 border-black rounded-none"
                  placeholder="521234567890"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowRaffleModal(false)}
                className="flex-1 bg-white text-black font-bold uppercase tracking-wider border-2 border-black px-6 py-3 shadow-[4px_4px_0_0_#000000] hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 bg-[#FF3333] text-white font-bold uppercase tracking-wider border-2 border-black px-6 py-3 shadow-[4px_4px_0_0_#000000] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_0_#000000] transition-all"
              >
                {editingRaffle ? 'Actualizar' : 'Crear Sorteo'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
